/* Header navigation owns its listeners, but never owns the cart's scroll lock. */
(() => {
  const initializedHeaders = new WeakSet();
  const instances = new WeakMap();
  const within = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];

  const initHeader = (header) => {
    if (initializedHeaders.has(header)) return;
    const nav = header.querySelector('[data-primary-nav]');
    const burger = header.querySelector('[data-menu-toggle]');
    if (!nav) return;
    const controller = new AbortController();
    const { signal } = controller;
    const compactQuery = window.matchMedia('(max-width: 1024px)');
    const hoverQuery = window.matchMedia('(hover: hover)');
    const desktopHover = () => hoverQuery.matches && !compactQuery.matches;
    const listen = (target, type, handler) => target?.addEventListener(type, handler, { signal });
    const focus = (element) => element?.focus({ preventScroll: true });
    let openEntry = null;

    const entries = [...header.querySelectorAll('[data-mega-root]')]
      .filter((root) => root.closest('[data-site-header]') === header)
      .map((root) => {
        const button = root.querySelector('[data-mega-toggle]');
        const controls = button?.getAttribute('aria-controls');
        const menus = [...root.querySelectorAll('[data-mega-menu]')];
        const menu = controls ? menus.find((item) => item.id === controls) : menus[0];
        if (!button || !menu) return null;
        const tablist = menu.querySelector('[data-zm-tabs]');
        const panels = [...menu.querySelectorAll('[data-zm-panel]')];
        const tabs = [...(tablist?.querySelectorAll('[data-zm-tab]') || [])]
          .filter((tab) => tab.closest('[data-zm-tabs]') === tablist);
        return { root, button, menu, tablist, tabs, panels, closeTimer: null };
      }).filter(Boolean);

    const cancelClose = (entry) => {
      if (entry.closeTimer !== null) window.clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    };
    const closeMenu = (entry, restoreFocus = false) => {
      cancelClose(entry);
      // Move focus before hiding its current panel, so mobile Esc does not
      // briefly blur outside the header and close the outer navigation too.
      if (restoreFocus) focus(entry.button);
      entry.menu.hidden = true;
      entry.root.classList.remove('is-open');
      entry.button.setAttribute('aria-expanded', 'false');
      if (openEntry === entry) openEntry = null;
    };
    const closeNav = (restoreFocus = false) => {
      entries.forEach((entry) => closeMenu(entry));
      nav.classList.remove('is-open');
      burger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) focus(burger);
    };
    const openMenu = (entry) => {
      const wasHidden = entry.menu.hidden;
      entries.forEach((other) => { if (other !== entry) closeMenu(other); });
      cancelClose(entry);
      entry.menu.hidden = false;
      if (wasHidden) entry.menu.scrollTop = 0;
      entry.root.classList.add('is-open');
      entry.button.setAttribute('aria-expanded', 'true');
      openEntry = entry;
    };
    const selectTab = (entry, tab) => {
      if (!entry.tabs.includes(tab)) return;
      const controls = tab.getAttribute('aria-controls');
      const panel = entry.panels.find((item) => item.dataset.zmPanel === tab.dataset.zmTab && (!controls || item.id === controls));
      if (!panel) return;
      entry.tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      entry.panels.forEach((item) => { item.hidden = item !== panel; });
    };

    entries.forEach((entry) => {
      closeMenu(entry);
      entry.tablist?.setAttribute('role', 'tablist');
      entry.tablist?.setAttribute('aria-orientation', 'vertical');
      const firstTab = entry.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || entry.tabs[0];
      if (firstTab) selectTab(entry, firstTab);

      listen(entry.root, 'mouseenter', () => {
        if (desktopHover()) openMenu(entry);
      });
      listen(entry.root, 'mouseleave', () => {
        if (!desktopHover()) return;
        cancelClose(entry);
        entry.closeTimer = window.setTimeout(() => {
          entry.closeTimer = null;
          // Do not hide a menu while a keyboard user is still working in it.
          if (!entry.root.contains(document.activeElement)) closeMenu(entry);
        }, 200);
      });
      listen(entry.menu, 'mouseenter', () => cancelClose(entry));
      listen(entry.root, 'focusout', (event) => {
        if (!entry.root.contains(event.relatedTarget)) closeMenu(entry);
      });
      listen(entry.button, 'click', (event) => {
        if (desktopHover() && event.detail > 0) openMenu(entry);
        else if (entry.menu.hidden) openMenu(entry);
        else closeMenu(entry);
      });
      // Top-level focus alone never opens a menu (including focus restored by Esc).
      listen(entry.button, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openMenu(entry);
        const first = entry.tabs[0];
        if (first) {
          selectTab(entry, first);
          focus(first);
        } else focus(entry.menu.querySelector('a[href], button:not([disabled])'));
      });

      entry.tabs.forEach((tab) => {
        listen(tab, 'click', () => selectTab(entry, tab));
        listen(tab, 'focus', () => { if (!entry.menu.hidden) selectTab(entry, tab); });
        listen(tab, 'mouseenter', () => { if (desktopHover() && !entry.menu.hidden) selectTab(entry, tab); });
      });
      listen(entry.tablist, 'keydown', (event) => {
        const index = entry.tabs.indexOf(event.target.closest('[data-zm-tab]'));
        if (index < 0 || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? entry.tabs.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + entry.tabs.length) % entry.tabs.length;
        selectTab(entry, entry.tabs[next]);
        focus(entry.tabs[next]);
      });
    });

    listen(burger, 'click', () => {
      if (nav.classList.contains('is-open')) closeNav();
      else {
        nav.classList.add('is-open');
        nav.scrollTop = 0;
        burger.setAttribute('aria-expanded', 'true');
      }
    });
    listen(header, 'focusout', (event) => {
      if (!header.contains(event.relatedTarget)) closeNav();
    });
    listen(document, 'click', (event) => {
      if (!header.contains(event.target)) {
        closeNav();
        return;
      }
      if (openEntry && !openEntry.root.contains(event.target)) closeMenu(openEntry);
      if (compactQuery.matches && nav.classList.contains('is-open') && !nav.contains(event.target) && !burger?.contains(event.target)) closeNav();
    });
    listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target.closest?.('[data-cart-drawer]') || document.querySelector('[data-cart-drawer].is-open')) return;
      if (openEntry) {
        event.preventDefault();
        closeMenu(openEntry, true);
      } else if (nav.classList.contains('is-open')) {
        event.preventDefault();
        closeNav(true);
      }
    });
    const viewportChanged = () => {
      const active = document.activeElement;
      const focusInNav = nav.contains(active) || active === burger;
      const focusedEntry = entries.find((entry) => entry.root.contains(active));
      closeNav();
      if (focusInNav) focus(compactQuery.matches ? burger : focusedEntry?.button || entries[0]?.button);
    };
    listen(window, 'resize', viewportChanged);
    listen(compactQuery, 'change', viewportChanged);
    listen(hoverQuery, 'change', viewportChanged);

    initializedHeaders.add(header);
    instances.set(header, () => {
      controller.abort();
      closeNav();
      initializedHeaders.delete(header);
      instances.delete(header);
    });
    closeNav();
  };

  const init = (scope = document) => within(scope, '[data-site-header]').forEach(initHeader);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    within(event.target, '[data-site-header]').forEach((header) => instances.get(header)?.());
  });
})();
